import { ANIMATION_SETTINGS } from "../settings/animation";
import { delegate } from "../tools/event";

export const CLASS_OPEN = 'is-light-box-open';

const SEL_TRIGGER = '[data-light-box-trigger]';
const ATTR_TRIGGER = 'data-light-box-trigger';

// Set up the inner template
const tmpl = document.createElement('template');

const closeIcon  = `<svg class="close-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 22 22"><g stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"><path d="M17.188 4.813 4.813 17.188M17.188 17.188 4.813 4.813"/></g></svg>`;
tmpl.innerHTML = `
  <style>
    * { box-sizing: border-box; }

    .container {
      display: none;
      justify-content: stretch;
      align-items: stretch;
    }

    .close-icon {
      cursor: pointer;
      width: 1.25rem;
      right: 0;
      top: 0;
      left: 0;
      bottom: 0;
      margin: auto;
    }

    .o-btn.is-close {
      background-color: #FCFCFB;
      border: none;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      position: fixed;
      width: 2.5rem;
      height: 2.5rem;
      right: 1rem;
      top: 1rem;
      z-index: 2;
      color: black;

      @media only screen and (max-width: 46.8125rem) {
        top: -.75rem;
        right: -.75rem;
      }
    }
    
    .o-btn.is-close:hover {
      background-color: #3F7355;
      color: #FCFCFB;
    }

    .background {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      transition: all ${ANIMATION_SETTINGS.timeDefault};
    }

    .wrapper {
      position: fixed;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
      overflow-y: auto;
      max-width: 100vw;
      max-height: 100vh;
    }

    .inner {
      padding: 1rem;
      width: 100%;
      min-height: 100%;
      display: flex;
      justify-content: flex-end;
    }

    .popup {
      box-sizing: border-box;
      position: relative;
      width: auto;
      max-width: 87.5rem;
      padding: 1rem;
      margin: auto;
      opacity: 0;
      transform: translateY(-2rem);
      transition: all ${ANIMATION_SETTINGS.timeDefault} ${ANIMATION_SETTINGS.easeOut};
    }

    .popup.small { max-width: 30rem; }
    .popup.large { max-width: 80rem; }
    .container.is-visible { display: block; }

    .is-closing .popup { transform: translateY(2rem); }
    .is-open .background { opacity: 1; }
    .is-open .popup {
      opacity: 1;
      transform: translateY(0);
    }
  </style>

  <div part="root" class="container">
    <div part="background" class="background"></div>
    <div part="wrapper" class="wrapper">
      <div part="inner" class="inner">
        <div part="popup" class="popup">
          <button class="o-btn is-close">${closeIcon}</button>
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
`;

export type LightBoxSize = 'small' | 'medium' | 'large';

export class LightBoxElement extends HTMLElement {
  public static get observedAttributes() {
    return [ 'visible', 'closable', 'size' ];
  }

  public static confirm = (params:{ yes:string, no:string, title:string }) => {
    // const lb = document.createElement('light-box') as LightBoxElement;
    const lb = new LightBoxElement();
    document.body.appendChild(lb);
    lb.classList.add('o-light-box__confirm');
    lb.size = 'small';

    const buttons = document.createElement('div');
    buttons.classList.add('o-light-box__buttons');

    
    const btnYes = document.createElement('button');
    btnYes.type = 'button';
    btnYes.classList.add('o-btn');
    btnYes.textContent = params.yes;

    const btnNo = document.createElement('button');
    btnNo.type = 'button';
    btnNo.classList.add('o-btn');
    btnNo.textContent = params.no;

    const title = document.createElement('h2');
    title.textContent = params.title;

    lb.appendChild(title);
    lb.appendChild(buttons);
    buttons.appendChild(btnYes);
    buttons.appendChild(btnNo);

    lb.visible = true;

    const decided = () => {
      btnYes.disabled = true;
      btnYes.classList.add('is-disabled');
      btnNo.disabled = true;
      btnNo.classList.add('is-disabled');

      lb.visible = false;
      setTimeout(() => lb.remove(), ANIMATION_SETTINGS.durationDefault);
    }

    return new Promise<boolean>((resolve,reject) => {
      btnYes.addEventListener('click', () => {
        resolve(true);
        decided();
      });

      btnNo.addEventListener('click', () => {
        resolve(false);
        decided();
      });
    })
  }

  // Getters and setters
  get visible():boolean {
    return this.getAttribute('visible') == 'true' ? true : false;
  }

  set visible(value:boolean) {
    this.setAttribute('visible', value+'');
    this.updateVisible();
  }

  get closable() {
    return this.hasAttribute('closable') ? (
      this.getAttribute('closable') == 'true' 
    ) : true;
  }

  set closable(value:boolean) {
    this.setAttribute('closable', value+'');
  }

  get size():LightBoxSize {
    return this.getAttribute('size') as LightBoxSize || 'medium';
  }

  set size(value:LightBoxSize) {
    this.setAttribute('size', value);
    this.updateSize();
  }

  get padded():boolean {
    return this.hasAttribute('padded')?this.getAttribute('padded')=='true':true;
  }

  set padded(value:boolean) {
    this.setAttribute('padded', value+'');
    this.updatePadded();
  }

  // Properties
  private readonly root:HTMLDivElement;
  private readonly inner:HTMLDivElement;
  private readonly popup:HTMLDivElement;
  private readonly parent:HTMLElement|null;
  private readonly close:HTMLDivElement | null;

  private timeout:NodeJS.Timeout|null;

  constructor() {
    super();

    this.timeout = null;

    // Set up the shadow
    const sr = this.attachShadow({ mode: 'open' });
    sr.appendChild(tmpl.content.cloneNode(true));

    // Get the parts
    this.root = sr.querySelector<HTMLDivElement>('.container')!;
    this.inner = sr.querySelector<HTMLDivElement>('.inner')!;
    this.popup = sr.querySelector<HTMLDivElement>('.popup')!;
    this.parent = this.parentElement||null;
    this.close = sr.querySelector<HTMLDivElement>('.close-icon')!;


    this.close.addEventListener('click', () => {
      this.visible = false;
    })

    document.addEventListener('keydown', this.onKeyPress);
    delegate({
      callback: this.onDocumentClick,
      selector: SEL_TRIGGER,
      element: document,
      event: 'click'
    });
    this.inner.addEventListener('click', this.onBackgroundClick)

    // Reset state
    this.updateVisible();
  }

  private updateVisible() {
    if(this.timeout) clearTimeout(this.timeout);

    if(this.visible) {
      document.body.classList.add(CLASS_OPEN);
      this.root.classList.add('is-visible');
      this.root.classList.remove('is-closing');
      document.body.appendChild(this);

      this.timeout = setTimeout(() => this.root.classList.add('is-open'), 16);

      //@ts-ignore
      this.root.part.add('visible');
    } else {
      document.body.classList.remove(CLASS_OPEN);
      (this.parent||document.body).appendChild(this);
      
      setTimeout(() => {
        this.root.classList.remove('is-open');
        this.root.classList.add('is-closing');
      }, 16);
      
      this.timeout = setTimeout(() => {
        this.root.classList.remove('is-visible');
        this.root.classList.remove('is-closing');
      }, ANIMATION_SETTINGS.durationDefault);
      
      //@ts-ignore
      this.root.part.remove('visible');
    }
  }

  private updateSize() {
    this.popup.classList.remove('small');
    this.popup.classList.remove('large');

    switch(this.size) {
      case 'small':
        this.popup.classList.add('small');
        break;
      case 'large':
        this.popup.classList.add('large');
        break;
      case 'medium':
        break;
    }
  }

  private updatePadded() {
    if(this.padded) {
      //@ts-ignore
      this.popup.part.remove('unpadded');
    } else {
      //@ts-ignore
      this.popup.part.add('unpadded');
    }
  }
  
  // Events
  public onKeyPress = (e:KeyboardEvent) => {
    if(this.closable && e.key == 'Escape') {
      this.visible = false;
    }
  }

  public onBackgroundClick = (e:MouseEvent) => {
    if(e.target !== this.inner) return;
    if(this.closable) this.visible = false;
  }
  
  public onDocumentClick = (e:MouseEvent, target:HTMLElement) => {
    const attr = target.getAttribute(ATTR_TRIGGER);
    if(!attr) return;
    if(!this.matches(attr)) return;
    e.preventDefault();
    this.visible = this.closable ? !this.visible : true;
  }

  public attributeChangedCallback(attr:string, oldValue:any, newValue:any) {
    switch(attr) {
      case 'visible':
        this.updateVisible();
        break;
      case 'size':
        this.updateSize();
        break;
      case 'padded':
        this.updatePadded();
        break;
    }
  }
}

if(!customElements.get('light-box')) {
  customElements.define('light-box', LightBoxElement);
}